// WebSocket signalling client: request/response over JSON plus server events.
// Reconnection is driven by Session (it must also rebuild media), so this
// class only reports the close.

import type { Events, Methods } from '../../../shared/src/protocol';

type Handler<E extends keyof Events> = (data: Events[E]) => void;

export class Signalling {
  private ws: WebSocket | null = null;
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: number }>();
  private handlers = new Map<string, Set<(data: unknown) => void>>();
  onclose: ((code: number, reason: string) => void) | null = null;

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${location.host}/ws`);
      this.ws = ws;
      const timeout = window.setTimeout(() => {
        ws.close();
        reject(new Error('connection timed out'));
      }, 8000);
      ws.onopen = () => {
        clearTimeout(timeout);
        resolve();
      };
      ws.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('cannot reach the talkback server'));
      };
      ws.onclose = (ev) => {
        clearTimeout(timeout);
        for (const p of this.pending.values()) {
          clearTimeout(p.timer);
          p.reject(new Error('connection lost'));
        }
        this.pending.clear();
        if (this.ws === ws) this.onclose?.(ev.code, ev.reason);
      };
      ws.onmessage = (ev) => {
        let msg: { id?: number; ok?: boolean; data?: unknown; error?: string; event?: string };
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (typeof msg.id === 'number') {
          const p = this.pending.get(msg.id);
          if (!p) return;
          this.pending.delete(msg.id);
          clearTimeout(p.timer);
          if (msg.ok) p.resolve(msg.data);
          else p.reject(new Error(msg.error ?? 'request failed'));
        } else if (msg.event) {
          this.handlers.get(msg.event)?.forEach((h) => h(msg.data));
        }
      };
    });
  }

  request<M extends keyof Methods>(method: M, params: Methods[M]['params']): Promise<Methods[M]['result']> {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return Promise.reject(new Error('not connected'));
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`${method} timed out`));
      }, 10_000);
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      ws.send(JSON.stringify({ id, method, params }));
    });
  }

  on<E extends keyof Events>(event: E, handler: Handler<E>): () => void {
    let set = this.handlers.get(event);
    if (!set) this.handlers.set(event, (set = new Set()));
    set.add(handler as (d: unknown) => void);
    return () => set!.delete(handler as (d: unknown) => void);
  }

  close(): void {
    const ws = this.ws;
    this.ws = null;
    ws?.close();
  }

  get open(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
